import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { User, Mail, MapPin, MessageSquare, HelpCircle, ArrowRight, CheckCircle2, TrendingUp, ChevronDown } from 'lucide-react';
import UseAxiosPublic from '../hooks/UseAxiosPublic';

const ContactForm = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const axiosPublic = UseAxiosPublic();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            address: '',
            requestType: 'Technical support',
            message: ''
        }
    });

    const onSubmit = async (data) => {
        setErrorMsg('');

        try {
            const response = await axiosPublic.post('/contact', data);

            if (response.data.success) {
                setIsSubmitted(true);

                setTimeout(() => {
                    setIsSubmitted(false);
                    reset();
                }, 3000);
            }
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || err.message || 'Something went wrong');
        }
    };

    return (
        /* Stacks vertically (Form on top, Branding below) on mobile/tablet, side-by-side on desktop */
        <div className="min-h-screen w-full flex flex-col-reverse lg:flex-row bg-slate-950 font-sans text-slate-300 selection:bg-blue-500/30">
            
            {/* ==================================================== */}
            {/* BRANDING SECTION (Below form on Mobile, Left on Desktop) */}
            {/* ==================================================== */}
            <div className="w-full lg:w-1/2 relative flex flex-col justify-between overflow-hidden border-t lg:border-t-0 lg:border-r border-slate-800/50 py-10 lg:py-0">
                
                {/* Background Image & Dark Overlay */}
                <div 
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40 lg:opacity-100"
                    style={{ backgroundImage: "url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2000&auto=format&fit=crop')" }}
                ></div>
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-blue-950/80 backdrop-blur-[2px]"></div>

                {/* Brand Content */}
                <div className="relative z-10 px-6 sm:px-10 lg:px-12 pt-4 lg:pt-24">
                    <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-white tracking-tight leading-tight mb-4 sm:mb-6">
                        Empower your <br className="hidden sm:block"/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                            restaurant operations.
                        </span>
                    </h1>
                    <p className="text-slate-400 text-sm sm:text-lg max-w-md font-medium">
                        Whether you need technical support, have a feature request, or want to expand your branches, our team is here to help.
                    </p>

                    <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
                        {['24/7 Priority Support', 'Enterprise Grade Security', 'Dedicated Account Manager'].map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-slate-300 text-xs sm:text-base font-medium">
                                <CheckCircle2 size={18} className="text-blue-500 shrink-0" />
                                {feature}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Status Graphic Widget */}
                <div className="relative z-10 px-6 sm:px-10 lg:px-12 pt-8 lg:pt-0 pb-4 lg:pb-12 lg:mt-auto">
                    <div className="bg-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 shadow-2xl max-w-md">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg">
                                    <TrendingUp size={16} className="text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider">System Status</p>
                                    <p className="text-xs sm:text-sm font-bold text-white">All Branches Online</p>
                                </div>
                            </div>
                            <span className="flex h-3 w-3 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                            </span>
                        </div>
                        <div className="h-2 w-full bg-slate-950 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 w-full"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================================================== */}
            {/* CONTACT FORM (Top on Mobile, Right on Desktop)      */}
            {/* ==================================================== */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-8 lg:px-12 pt-20 sm:pt-24 lg:pt-24 pb-10 relative overflow-hidden">
                
                <div className="absolute top-0 right-0 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-blue-500/5 blur-[100px] rounded-full pointer-events-none"></div>

                <div className="w-full max-w-xl relative z-10">
                    
                    <div className="mb-6 sm:mb-8 lg:mb-10">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-2">Get in touch</h2>
                        <p className="text-slate-400 text-xs sm:text-sm">
                            Send us a message and we'll get back to you shortly.
                        </p>
                    </div>

                    {errorMsg && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-400 text-sm">
                            {errorMsg}
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
                        
                        {/* Name & Email Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                            
                            {/* Full Name */}
                            <div className="space-y-1.5">
                                <label htmlFor="name" className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                                    Full Name
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <User size={18} className="text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                    </div>
                                    <input
                                        type="text"
                                        id="name"
                                        {...register('name', { required: 'Full name is required' })}
                                        className={`w-full bg-slate-900/50 border ${errors.name ? 'border-red-500' : 'border-slate-800'} rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all shadow-sm text-sm`}
                                        placeholder="John Doe"
                                    />
                                </div>
                                {errors.name && <p className="text-xs text-red-400 ml-1">{errors.name.message}</p>}
                            </div>

                            {/* Email */}
                            <div className="space-y-1.5">
                                <label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                                    Email Address
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail size={18} className="text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        {...register('email', { 
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                        className={`w-full bg-slate-900/50 border ${errors.email ? 'border-red-500' : 'border-slate-800'} rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all shadow-sm text-sm`}
                                        placeholder="john@example.com"
                                    />
                                </div>
                                {errors.email && <p className="text-xs text-red-400 ml-1">{errors.email.message}</p>}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-1.5">
                            <label htmlFor="address" className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                                Address / Branch Location
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <MapPin size={18} className="text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <input
                                    type="text"
                                    id="address"
                                    {...register('address', { required: 'Address is required' })}
                                    className={`w-full bg-slate-900/50 border ${errors.address ? 'border-red-500' : 'border-slate-800'} rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all shadow-sm text-sm`}
                                    placeholder="123 Main St, City, Country"
                                />
                            </div>
                            {errors.address && <p className="text-xs text-red-400 ml-1">{errors.address.message}</p>}
                        </div>

                        {/* Request Type */}
                        <div className="space-y-1.5">
                            <label htmlFor="requestType" className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                                Request Type
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                    <HelpCircle size={18} className="text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <select
                                    id="requestType"
                                    {...register('requestType')}
                                    className="w-full appearance-none bg-slate-900/50 border border-slate-800 rounded-xl py-3 pl-11 pr-10 text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all shadow-sm cursor-pointer relative text-sm"
                                >
                                    <option value="Technical support" className="bg-slate-900">Technical support</option>
                                    <option value="Billing & subscriptions" className="bg-slate-900">Billing & subscriptions</option>
                                    <option value="New project" className="bg-slate-900">New project</option>
                                    <option value="Feature request" className="bg-slate-900">Feature request</option>
                                    <option value="General inquiry" className="bg-slate-900">General inquiry</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                    <ChevronDown size={16} className="text-slate-500" />
                                </div>
                            </div>
                        </div>

                        {/* Message */}
                        <div className="space-y-1.5">
                            <label htmlFor="message" className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                                Additional Details
                            </label>
                            <div className="relative group">
                                <div className="absolute top-3.5 left-0 pl-4 flex items-start pointer-events-none">
                                    <MessageSquare size={18} className="text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <textarea
                                    id="message"
                                    rows="4"
                                    {...register('message', { required: 'Details/message is required' })}
                                    className={`w-full bg-slate-900/50 border ${errors.message ? 'border-red-500' : 'border-slate-800'} rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all shadow-sm resize-none text-sm`}
                                    placeholder="Please provide any relevant details or context..."
                                ></textarea>
                            </div>
                            {errors.message && <p className="text-xs text-red-400 ml-1">{errors.message.message}</p>}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting || isSubmitted}
                            className={`w-full relative overflow-hidden flex items-center justify-center gap-2 py-3.5 mt-2 rounded-xl font-bold text-white transition-all duration-300 text-sm sm:text-base ${
                                isSubmitted 
                                    ? 'bg-emerald-500 hover:bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                                    : 'bg-blue-600 hover:bg-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] hover:-translate-y-0.5'
                            } disabled:opacity-80 disabled:cursor-not-allowed`}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Sending...
                                </>
                            ) : isSubmitted ? (
                                <>
                                    <CheckCircle2 size={18} />
                                    Message Sent Successfully!
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactForm;